import { Injectable, signal, computed } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { BannersService } from '../banners.service'
import { BannersRepository } from '../banners.repository'
import { Banner } from '../models/banner.entity'
import { FilesService } from '../../files'
import { lastValueFrom } from 'rxjs'

interface BannerDetailState {
  banner?: Banner
  error?: HttpErrorResponse
  isLoading: boolean
  isModified: boolean
}

const BANNER_DETAIL_INITIAL_STATE: BannerDetailState = {
  isLoading: false,
  isModified: false,
}

@Injectable()
export class BannerDetailStore {
  constructor(
    private bannersService: BannersService,
    private bannersRepository: BannersRepository,
    private filesService: FilesService,
  ) {}

  private state = signal<BannerDetailState>(BANNER_DETAIL_INITIAL_STATE)

  // Selectors
  readonly banner = computed(() => this.state().banner)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly isModified = computed(() => this.state().isModified)
  readonly isErrored = computed(() => !!this.state().error)

  // Effects

  async load(bannerId?: string): Promise<void> {
    if (!bannerId) {
      return
    }
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.bannersService.read$(bannerId).subscribe({
        next: async (data) => {
          resolve()
          this.setData(
            data,
            this.state().banner?._id
              ? this.state().banner?._id !== data._id
              : false,
          )
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  async save(banner: Banner, notify?: boolean): Promise<boolean> {
    this.unsetError()

    return new Promise((resolve) => {
      this.bannersService.upsert$(banner).subscribe({
        next: (data) => {
          resolve(true)
          this.setData(data, false)
          if (notify === true) {
            this.bannersRepository.notify({ code: 'BANNER_SAVED' })
          }
        },
        error: (error) => {
          resolve(false)
          this.setError(error)
        },
      })
    })
  }

  async addImage(fileId?: string) {
    const banner = this.banner()
    if (!banner) {
      this.bannersRepository.notify({ code: 'BANNER_NOT_FOUND' })
      return
    }
    if (!fileId) {
      this.bannersRepository.notify({ code: 'FILE_NOT_FOUND' })
      return
    }
    this.unsetError()

    return new Promise((resolve) => {
      this.bannersService.attachFile$(banner._id, fileId).subscribe({
        next: (data) => {
          resolve(true)
          this.setData(data, false)
          this.bannersRepository.notify({ code: 'ATTACHMENT_UPLOADED' })
        },
        error: (error) => {
          resolve(false)
          this.setError(error)
        },
      })
    })
  }

  async removeImage(): Promise<boolean> {
    const banner = this.banner()
    if (!banner) {
      this.bannersRepository.notify({ code: 'BANNER_NOT_FOUND' })
      return false
    }
    if (!banner.file?._id) {
      this.bannersRepository.notify({ code: 'FILE_NOT_FOUND' })
      return false
    }
    this.unsetError()

    try {
      const fileId = banner.file._id

      // First detach the file from the banner
      const updatedBanner = await lastValueFrom(
        this.bannersService.detachFile$(banner._id, fileId),
      )

      // Update state with the banner that no longer has the file reference
      this.setData(updatedBanner, false)

      // Then delete the actual file
      await lastValueFrom(this.filesService.delete$(fileId))

      this.bannersRepository.notify({ code: 'ATTACHMENT_DELETED' })
      return true
    } catch (error) {
      this.setError(error as HttpErrorResponse)
      return false
    }
  }

  // Reducers

  setData(banner: Banner, isModified?: boolean) {
    this.state.set({
      ...this.state(),
      banner,
      isModified: isModified ?? this.state().isModified,
      isLoading: false,
      error: undefined,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }
}
